import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Tag,
  Dropdown,
  Badge,
  Tooltip,
  App,
  theme,
  Typography,
  Checkbox,
} from 'antd';
import type { TableProps } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  MoreOutlined,
  EditOutlined,
  CheckCircleOutlined,
  StopOutlined,
  InboxOutlined,
} from '@ant-design/icons';

const { Title } = Typography;
import { useCustomersTable, useCustomerMutations, useAllCategoriesForColorMap } from '@/hooks';
import type { CustomerListItem } from '@/types/api/customer';
import { useCustomerTranslation } from '@/hooks/useCustomerTranslation';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import TableContainer from '@/components/common/TableContainer';
import CategoryTag from '@/components/common/CategoryTag';
import CustomerFilterPopover from './CustomerFilterPopover';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useCustomerStore } from '@/stores/customerStore';
import { buildCategoryColorMap, normalizeVendorCategories } from '@/utils/categoryUtils';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { HighlightText } from '@/components/common/HighlightText';

interface CustomerListPageProps {
  compact?: boolean; // If true, hide header and filters
  initialCategories?: number[]; // Pre-filter by category IDs
  showCategoryColumn?: boolean; // If false, hide category column
  defaultActiveOnly?: boolean; // If true, default to showing only active customers
}

const CustomerListPage = ({
  compact = false,
  initialCategories,
  showCategoryColumn = true,
  defaultActiveOnly = false,
}: CustomerListPageProps = {}) => {
  const navigate = useNavigate();
  const { t } = useCustomerTranslation();
  const { token } = theme.useToken();
  const { message } = App.useApp();

  // Use Zustand store for filters
  const { filters, setSearch, setCategoryIds, setCountry, setIsActive } = useCustomerStore();

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  // Confirmation dialog for activate/deactivate customer
  const toggleStatusDialog = useConfirmationDialog<CustomerListItem>();

  // Set initial category filter when provided, or reset when not in compact mode
  useEffect(() => {
    if (initialCategories && initialCategories.length > 0) {
      setCategoryIds(initialCategories);
    } else if (!compact) {
      setCategoryIds([]);
    }
  }, [initialCategories, compact, setCategoryIds]);

  // Set active-only filter when defaultActiveOnly is enabled (e.g., in category detail page)
  useEffect(() => {
    if (defaultActiveOnly) {
      setIsActive(true);
    }
  }, [defaultActiveOnly, setIsActive]);

  // Fetch customers using useAntdTable hook
  const { tableProps, isLoading, error, refresh } = useCustomersTable({
    search: filters.search,
    categoryIds: filters.categoryIds,
    country: filters.country,
    isActive: filters.isActive,
  });

  // Fetch ALL categories for color mapping
  const { categories: allCategories } = useAllCategoriesForColorMap();

  // Build category color map for efficient lookups
  const categoryColorMap = useMemo(() => buildCategoryColorMap(allCategories), [allCategories]);

  // Customer mutations
  const { toggleStatus } = useCustomerMutations();

  // Handle activate/deactivate with confirmation
  const handleToggleStatus = (customer: CustomerListItem) => {
    toggleStatusDialog.open(customer);
  };

  const baseColumns: TableProps<CustomerListItem>['columns'] = [
    {
      title: t('customer.fields.customerId'),
      dataIndex: 'customerId',
      key: 'customerId',
      width: 120,
      sorter: true,
      showSorterTooltip: false,
      render: (customerId: number) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>
          <HighlightText text={customerId} searchTerm={filters.search} fallback="-" />
        </span>
      ),
    },
    {
      title: t('customer.fields.customerName'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 250,
      sorter: true,
      showSorterTooltip: false,
      render: (_: string, record: CustomerListItem) => {
        const hasSecondaryInfo = record.nickname || record.alternativeName;

        return (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span style={{ fontWeight: token.fontWeightStrong }}>
              <HighlightText text={record.customerName} searchTerm={filters.search} fallback="-" />
            </span>
            {hasSecondaryInfo && (
              <span
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorTextSecondary,
                  lineHeight: 1.3,
                }}
              >
                {record.nickname && record.alternativeName ? (
                  <>
                    <HighlightText text={record.nickname} searchTerm={filters.search} fallback="" />
                    {' · '}
                    <HighlightText text={record.alternativeName} searchTerm={filters.search} fallback="" />
                  </>
                ) : (
                  <HighlightText text={record.nickname || record.alternativeName} searchTerm={filters.search} fallback="" />
                )}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: t('customer.fields.category'),
      dataIndex: 'categories',
      key: 'categories',
      width: 180,
      sorter: false,
      render: (categories: CustomerListItem['categories']) => {
        if (!categories || categories.length === 0) return '-';

        // Normalize categories and map colors from category list
        const normalizedCategories = normalizeVendorCategories(categories, categoryColorMap);

        // Show first 2 categories, with +N indicator if more
        const visibleCategories = normalizedCategories.slice(0, 2);
        const remainingCategories = normalizedCategories.slice(2);
        const remainingCount = remainingCategories.length;

        return (
          <div className="tw-flex tw-gap-1 tw-flex-wrap tw-items-center">
            {visibleCategories.map((cat) => (
              <CategoryTag
                key={cat.categoryId || cat.category}
                name={cat.category}
                color={cat.color || token.colorPrimary}
              />
            ))}
            {remainingCount > 0 && (
              <Tooltip
                title={
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    {remainingCategories.map((cat) => (
                      <CategoryTag
                        key={cat.categoryId || cat.category}
                        name={cat.category}
                        color={cat.color || token.colorPrimary}
                      />
                    ))}
                  </div>
                }
                color={token.colorBgElevated}
                styles={{
                  body: {
                    color: token.colorText,
                  },
                }}
              >
                <Tag
                  bordered={false}
                  style={{
                    backgroundColor: token.colorFillQuaternary,
                    color: token.colorTextSecondary,
                    borderRadius: radiusTokens.control,
                    fontSize: token.fontSizeSM,
                    cursor: 'pointer',
                    border: 'none',
                    margin: 0,
                  }}
                >
                  +{remainingCount}
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: t('customer.fields.abn'),
      dataIndex: 'localTaxId',
      key: 'localTaxId',
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      render: (localTaxId: string) => <HighlightText text={localTaxId} searchTerm={filters.search} fallback="-" />,
    },
    {
      title: t('customer.fields.address'),
      dataIndex: 'address',
      key: 'address',
      width: 250,
      sorter: true,
      showSorterTooltip: false,
      render: (address: string) => address || '-',
    },
    {
      title: t('customer.fields.phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      render: (phoneNumber: string) => phoneNumber || '-',
    },
    {
      title: t('common:createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (createdAt: string) =>
        createdAt ? <TimestampDisplay date={createdAt} showLabel={false} /> : '-',
    },
    {
      title: t('common:updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 130,
      render: (updatedAt: string) =>
        updatedAt ? <TimestampDisplay date={updatedAt} showLabel={false} /> : '-',
    },
    {
      title: t('customer.fields.action'),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record: CustomerListItem) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: t('customer.actions.edit'),
                icon: <EditOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate(`/auth/customers/${record.customerId}/edit`);
                },
              },
              {
                type: 'divider',
              },
              {
                key: 'toggle-status',
                label: record.isActive
                  ? t('customer.actions.deactivate')
                  : t('customer.actions.activate'),
                icon: record.isActive ? <StopOutlined /> : <CheckCircleOutlined />,
                danger: record.isActive === true,
                style: !record.isActive ? { color: token.colorSuccess } : undefined,
                disabled: record.isActive === undefined || record.isActive === null,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleToggleStatus(record);
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  // Filter columns based on props
  const allColumns = showCategoryColumn
    ? baseColumns
    : baseColumns.filter((col) => col.key !== 'categories');

  // Count active filters
  // - categoryIds: count each selected category (skip in compact mode)
  // - country: count each location by splitting on comma
  // - isActive: count only when NOT default (true = Active is default, don't count)
  const countryCount = filters.country
    ? filters.country.split(',').filter((loc) => loc.trim()).length
    : 0;

  const activeFilterCount =
    (compact ? 0 : filters.categoryIds?.length || 0) +
    countryCount +
    (filters.isActive !== true ? 1 : 0);

  return (
    <div className={compact ? '' : 'tw-px-6 tw-pb-8'}>
      {/* Inactive row styling */}
      <style>
        {`
          /* Must target both tr AND td because Ant Design sets background on cells */
          /* Use .production-enhanced prefix for specificity (from TableContainer) */
          .production-enhanced .ant-table-tbody > tr.inactive-row,
          .production-enhanced .ant-table-tbody > tr.inactive-row > td {
            background-color: ${token.colorBgLayout} !important;
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row:hover > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:active > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:focus > td {
            background-color: ${token.colorFillQuaternary} !important;
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row > td:hover {
            background-color: ${token.colorFillQuaternary} !important;
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td:hover {
            background-color: ${token.colorBgLayout} !important;
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row td {
            color: ${token.colorTextTertiary} !important;
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row a {
            color: ${token.colorTextTertiary} !important;
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:hover,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:focus,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:active {
            color: ${token.colorTextTertiary} !important;
            background: transparent !important;
            border-color: transparent !important;
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row .anticon {
            color: ${token.colorTextTertiary} !important;
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn .anticon {
            color: ${token.colorTextTertiary} !important;
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-tag {
            opacity: 0.5;
            filter: grayscale(100%);
          }
          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="border-radius: 50%"],
          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="borderRadius"] {
            background-color: ${token.colorTextTertiary} !important;
            opacity: 0.5;
          }
        `}
      </style>

      {/* Include Inactive checkbox for compact mode */}
      {compact && (
        <div className="tw-mb-4 tw-flex tw-justify-end">
          <Checkbox
            checked={filters.isActive === null}
            onChange={(e) => setIsActive(e.target.checked ? null : true)}
          >
            {t('customer.labels.includeInactiveCustomers')}
          </Checkbox>
        </div>
      )}

      {/* Header Section */}
      {!compact && (
        <div className="tw-mb-6">
          <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
            {t('customer.list.title')}
          </h1>
        </div>
      )}

      {/* Search and Filter Section */}
      {!compact && (
        <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
          <Input
            placeholder={t('customer.list.search')}
            value={filters.search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="large"
            suffix={
              <SearchOutlined
                style={{
                  fontSize: token.sizeMD,
                  color: token.colorPrimary,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            }
            style={{
              width: '450px',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              transition: 'all 0.3s ease',
            }}
            styles={{
              input: {
                borderRadius: radiusTokens.card,
              },
            }}
          />
          <CustomerFilterPopover
            open={filterPopoverOpen}
            onOpenChange={setFilterPopoverOpen}
            onApply={(filterValues) => {
              const categoryIds = filterValues.categories?.map((c) => parseInt(c)) || [];
              setCategoryIds(categoryIds);
              setCountry(filterValues.country || '');

              if (filterValues.status === 'active') {
                setIsActive(true);
              } else if (filterValues.status === 'inactive') {
                setIsActive(false);
              } else {
                setIsActive(null);
              }
            }}
            initialFilters={{
              categories: filters.categoryIds?.map((id) => id.toString()) || [],
              country: filters.country,
              status:
                filters.isActive === true
                  ? 'active'
                  : filters.isActive === false
                    ? 'inactive'
                    : undefined,
            }}
          >
            <Badge count={activeFilterCount}>
              <Button
                icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
                size="large"
                style={{
                  borderRadius: radiusTokens.card,
                  height: `${controlHeightTokens.controlHeightXL}px`,
                  width: `${controlHeightTokens.controlHeightXL}px`,
                  border: `1px solid ${token.colorBorder}`,
                  transition: 'all 0.3s ease',
                }}
                styles={{
                  icon: { fontSize: token.sizeMD },
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = token.colorPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = token.colorBorder;
                }}
              />
            </Badge>
          </CustomerFilterPopover>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => navigate('/auth/customers/add')}
            style={{
              marginLeft: 'auto',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              paddingLeft: token.paddingLG,
              paddingRight: token.paddingLG,
            }}
          >
            {t('customer.list.create')}
          </Button>
        </div>
      )}

      {/* Table */}
      <TableContainer>
        <Table<CustomerListItem>
          {...tableProps}
          columns={allColumns}
          rowKey="customerId"
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => {
              navigate(`/auth/customers/${record.customerId}`);
            },
            style: {
              cursor: 'pointer',
            },
          })}
          locale={{
            emptyText: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: `${token.paddingXL * 2}px ${token.paddingXL}px`,
                  textAlign: 'center',
                }}
              >
                <InboxOutlined
                  style={{
                    fontSize: token.sizeXXL * 2,
                    color: token.colorTextDisabled,
                    marginBottom: token.marginLG,
                  }}
                />
                <Title
                  level={3}
                  style={{
                    marginBottom:
                      filters.search || activeFilterCount > 0 || compact ? 0 : token.marginLG,
                    color: token.colorText,
                  }}
                >
                  {filters.search || activeFilterCount > 0
                    ? t('customer.list.emptyWithFiltersTitle')
                    : t('customer.list.emptyNoDataTitle')}
                </Title>
                {!compact && !filters.search && activeFilterCount === 0 && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                    onClick={() => navigate('/auth/customers/add')}
                  >
                    {t('customer.list.createFirst')}
                  </Button>
                )}
              </div>
            ),
          }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: false,
          }}
          scroll={{ x: 1200 }}
        />
      </TableContainer>

      {/* Show error if fetch failed */}
      {error && (
        <div style={{ marginTop: token.margin, color: token.colorError }}>
          {t('customer.messages.fetchFailed')}: {error.message}
        </div>
      )}

      {/* Confirmation Dialog for Activate/Deactivate */}
      <ConfirmationDialog
        open={toggleStatusDialog.isOpen}
        onClose={toggleStatusDialog.close}
        onConfirm={async () => {
          const customer = toggleStatusDialog.data;
          if (customer && customer.isActive !== undefined && customer.isActive !== null) {
            try {
              const newStatus = !customer.isActive;

              await toggleStatus(customer.customerId, newStatus);

              const action = customer.isActive
                ? t('customer.status.inactive').toLowerCase()
                : t('customer.status.active').toLowerCase();
              message.success(t('customer.messages.customerActionSuccess', { action }));

              refresh();
            } catch (error) {
              message.error(t('customer.messages.statusUpdateFailed'));
              console.error('Error toggling customer status:', error);
            }
          }
          toggleStatusDialog.close();
        }}
        title={
          toggleStatusDialog.data?.isActive
            ? t('customer.modals.deactivateCustomerTitle')
            : t('customer.modals.activateCustomerTitle')
        }
        message={t('customer.messages.confirmStatusChange', {
          action: toggleStatusDialog.data?.isActive
            ? t('customer.actions.deactivate').toLowerCase()
            : t('customer.actions.activate').toLowerCase(),
          name: toggleStatusDialog.data?.customerName || '',
        })}
        type={toggleStatusDialog.data?.isActive ? 'danger' : 'info'}
        confirmText={
          toggleStatusDialog.data?.isActive
            ? t('customer.actions.deactivate')
            : t('customer.actions.activate')
        }
      />
    </div>
  );
};

export default CustomerListPage;
